import { from as observableFrom, Observable, of } from "rxjs";
import { config } from "@config";
import { switchMap, mergeMap, map, filter, tap, take, catchError } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { WalletService } from "./wallet.service";
import * as FileSaver from "file-saver";
import { Helper, parseList } from "@helpers/functions";
import { FiatTransactionNotifyService } from "@main-services/notify/fiat_transaction.notify.service";
import { SocketService } from "@main-services/socket.service";
import { AccountService } from "../../../account/services/account.service";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { currentDate } from "@helpers/date";
import { FiatTransaction } from "@models/transaction";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { ErrorClass, FiatWallet } from "@models";
import { DestructorService } from "@core-services/destructor.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { StatsigClientService, FEAT_GATES } from "@core-services/statsig/statsig.service";

const name = ServiceNames.FIAT_WALLET;

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class FiatWalletService extends WalletService<FiatWallet, FiatTransaction> {
	public name: string = ServiceNames.FIAT_WALLET;

	public constructor(
		protected destrucorService: DestructorService,
		private accountService: AccountService,
		protected transactionNotifyService: FiatTransactionNotifyService,
		protected apicallService: ApicallService,
		protected infoService: InfoService,
		protected translationService: TranslationService,
		protected initService: InitService,
		private socketService: SocketService,
		private walletsBalancesFacade: WalletsBalancesFacadeService,
		private readonly statsigClientService: StatsigClientService
	) {
		super(initService, destrucorService, name);
	}

	public socketInit(): void {
		this.socketService
			.joinRoom<FiatTransaction>({
				name: "fiat_wallet_transactions",
				type: FiatTransaction,
				isPrivate: true
			})
			.pipe(untilDestroyed(this))
			.subscribe(
				(transaction) => {
					this.updateReceived(transaction);
					this.transactionNotifyService.show("new", transaction);
				},
				(err) => {
					this.infoService.setError(err);
				}
			);

		this.socketService
			.joinRoom<FiatWallet>({
				name: "fiat_wallets",
				type: FiatWallet,
				isPrivate: true
			})
			.pipe(untilDestroyed(this))
			.subscribe((wallet: FiatWallet) => {
				this.upsert(wallet);
				this.walletsBalancesFacade.refreshBalances$().toPromise();
			});
	}

	public async cancel(transaction: FiatTransaction): Promise<FiatTransaction> {
		const oldTransaction = new FiatTransaction();
		Object.assign(oldTransaction, transaction);

		const call = this.apicallService.deleteRequest(
			"fiatwallet/" + transaction.getFiatWalletId() + "/transaction/" + transaction.getId(),
			new ApicallOption(FiatTransaction)
		);

		return super.cancelSub(call, transaction, oldTransaction).toPromise();
	}

	public cancelById(walletId: string, transactionId: string): Observable<FiatTransaction> {
		return this.getTransaction(walletId, transactionId).pipe(mergeMap((tx) => observableFrom(this.cancel(tx))));
	}

	public updateWalletsDefaultFiat(): void {
		const wallets = [];
		const fiat = this.accountService.getDefaultFiat();
		if (!fiat) {
			return;
		}
		this.list.forEach((w) => {
			if (w.getFiatId() === fiat.getId()) {
				wallets.unshift(w);
			} else {
				wallets.push(w);
			}
		});
		this.list = wallets;
		this.initWallets();
	}

	public getTransactions(): Observable<FiatTransaction[]> {
		return this.apicallService.get("fiatwallets/transactions").pipe(map((res) => parseList(res, FiatTransaction)));
	}

	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public createDeposit(walletid: string, apiData: ApicallOption): Observable<any> {
		return this.statsigClientService.isFeatureActive(FEAT_GATES.DEPOSIT_MIGRATION_ENABLED).pipe(
			take(1),
			catchError(() => of(false)),
			switchMap((isDepositMigrationActive: boolean) => {
				let url: string = `fiatwallet/${walletid}/deposit`;
				if (isDepositMigrationActive) {
					url = "fiat/deposit";
					apiData.apiVersion = "v2";
				}
				return this.apicallService.post(url, apiData);
			})
		);
	}

	public initWallets(force?: boolean): Observable<FiatWallet[]> {
		const apicallOption = new ApicallOption(FiatWallet);
		if (this.list.length === 0 || force) {
			const sub = this.apicallService.get("fiatwallets", apicallOption);
			sub.subscribe(
				(wallets) => {
					this.list = wallets;
					this.updateWalletsDefaultFiat();
					this.initService.init(this.name);
					return sub;
				},
				(error) => Helper.handleError(error)
			);
			return sub;
		}
		return null;
	}

	public refetchFiatWallets(): void {
		this.apicallService
			.get("fiatwallets", new ApicallOption(FiatWallet))
			.pipe(take(1))
			.subscribe({
				next: (wallets: FiatWallet[]) => {
					this.list = wallets;
					this.updateWalletsDefaultFiat();
					this.walletsBalancesFacade.refreshBalances$().toPromise();
				}
			});
	}

	public isWebHandled(ftx: FiatTransaction): Promise<boolean> {
		const prodUrl = "https://web.bitpanda.com/";

		const checkUrl = (url: string): boolean => ftx.getPaymentLink().startsWith(url);

		return this.getPaymentUrl(ftx)
			.pipe(
				tap((url: string) => ftx.setPaymentLink(url)),
				map(() => checkUrl(config.appUrl) || checkUrl(window.origin) || checkUrl(prodUrl))
			)
			.toPromise();
	}

	private getPaymentUrl(ftx: FiatTransaction): Observable<string> {
		return this.statsigClientService.isFeatureActive(FEAT_GATES.DEPOSIT_MIGRATION_ENABLED).pipe(
			take(1),
			catchError(() => of(false)),
			switchMap((isDepositMigrationActive: boolean) => {
				const option: ApicallOption = new ApicallOption();
				let url: string = `fiatwallet/${ftx.getFiatWalletId()}/deposit/${ftx.getId()}/pay`;
				if (isDepositMigrationActive) {
					url = `deposit/${ftx.getId()}/pay`;
					option.apiVersion = "v2";
				}
				return this.apicallService.get(url, option).pipe(map((a) => a["attributes"]["payment_url"]));
			})
		);
	}

	public handleExternalRedirect(ftx: FiatTransaction): Observable<"canceled" | "finished"> {
		window.open(ftx.getPaymentLink(), "_blank");
		return this.socketService
			.joinRoom<FiatTransaction>({
				name: "fiat_wallet_transactions",
				type: FiatTransaction,
				isPrivate: true
			})
			.pipe(
				filter((transition: FiatTransaction) => ftx.getId() === transition.getId()),
				filter(
					(transition: FiatTransaction) =>
						transition.getStatus() === "canceled" || transition.getStatus() === "finished"
				),
				map((transition: FiatTransaction) => transition.getStatus() as "canceled" | "finished")
			);
	}

	public download(wallet: FiatWallet, amount: number, paymentOptionId: string, userId: string): void {
		const url =
			"users/" +
			userId +
			"/payment-options/" +
			paymentOptionId +
			"/fiats/" +
			wallet.getCurrencyId() +
			"/information?amount=" +
			amount +
			"&pdf=1";
		this.apicallService.downloadFile(url, "application/pdf", new ApicallOption(), "v1").subscribe(
			(a) => FileSaver.saveAs(a, "Bitpanda_SEPA_" + currentDate() + ".pdf"),
			(error) => Helper.handleError(error)
		);
	}

	public getTransaction(walletId: string, txId: string): Observable<FiatTransaction> {
		return this.apicallService
			.get("fiatwallet/" + walletId + "/transaction/" + txId, new ApicallOption(FiatTransaction))
			.pipe(map((res: FiatTransaction[]) => res[0]));
	}

	public confirmTransaction(walletId: string, transactionId: string, code: string): Observable<FiatTransaction> {
		return this.apicallService
			.get(`fiatwallet/${walletId}/transaction/${transactionId}/confirm/${code}`)
			.pipe(switchMap(() => this.getTransaction(walletId, transactionId)));
	}

	public getWalletByFiatId(fiatId: string): FiatWallet {
		return this.list.find((wallet) => (!wallet ? false : wallet.getFiatId() === fiatId));
	}

	public resendAmazon(transaction: FiatTransaction): void {
		this.apicallService
			.get(
				"fiatwallet/" +
					transaction.getFiatWalletId() +
					"/transaction/" +
					transaction.getId() +
					"/resendAmazonGiftCode"
			)
			.subscribe(
				() =>
					this.infoService.setSuccess(
						this.translationService.translate("success_resend_amazon_voucher_to_email")
					),
				(e) => {
					if (e instanceof ErrorClass) {
						this.infoService.setError(e);
					} else {
						console.error(e);
					}
				}
			);
	}
}
